import { createContext, ReactNode, useContext, useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

type AppState = 'initializing' | 'authenticating' | 'checking-subscription' | 'ready' | 'needs-subscription';

interface AppStateContextType {
  appState: AppState;
  isReady: boolean;
  needsSubscription: boolean;
  subscriptionData: any;
}

const AppStateContext = createContext<AppStateContextType | null>(null);

export function AppStateProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading, isAuthenticated } = useAuth();
  const [appState, setAppState] = useState<AppState>('initializing');

  // Query subscription status only when user is authenticated
  const { data: subscriptionStatus, isLoading: subscriptionLoading } = useQuery({
    queryKey: ['/api/subscription-status'],
    queryFn: async () => {
      const response = await apiRequest('/api/subscription-status');
      return response;
    },
    enabled: !!user && isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Manage app state transitions
  useEffect(() => {
    if (authLoading) {
      setAppState('authenticating');
      return;
    }

    if (!isAuthenticated || !user) {
      setAppState('ready'); // Not authenticated is a valid ready state
      return;
    }

    if (subscriptionLoading) {
      setAppState('checking-subscription');
      return;
    }

    if (subscriptionStatus) {
      if (subscriptionStatus.redirectToSubscription === true || !subscriptionStatus.hasActiveSubscription) {
        setAppState('needs-subscription');
      } else {
        setAppState('ready');
      }
    } else {
      // Default to ready if we can't determine subscription status
      setAppState('ready');
    }
  }, [authLoading, isAuthenticated, user, subscriptionLoading, subscriptionStatus]);

  const contextValue = {
    appState,
    isReady: appState === 'ready',
    needsSubscription: appState === 'needs-subscription',
    subscriptionData: subscriptionStatus,
  };

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(AppStateContext);
  if (!context) {
    throw new Error("useAppState must be used within an AppStateProvider");
  }
  return context;
}